import {
  CellClickEventArgs,
  DragAndDrop,
  EventClickArgs,
  EventRenderedArgs,
  EventSettingsModel,
  Inject,
  NavigatingEventArgs,
  QuickInfoTemplatesModel,
  Resize,
  ResourceDetails,
  ResourceDirective,
  ResourcesDirective,
  ScheduleComponent,
  TimeScaleModel,
  TimelineMonth,
  TimelineViews,
  View,
  ViewDirective,
  ViewsDirective,
} from "@syncfusion/ej2-react-schedule";
import {
  LoadType,
  ShipmentLocationType,
  Status,
  TrailerType,
  TripAssetTypes,
  useGetOrganizationSettingsQuery,
} from "../../../graphql/generated";
import {
  AugmentedDriver,
  AugmentedShipment,
  PlanningToolProps,
} from "./PlanningTool";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addDays,
  addMilliseconds,
  addMinutes,
  addSeconds,
  differenceInSeconds,
  endOfDay,
  isBefore,
  setDay,
  startOfDay,
} from "date-fns";
import { capitalize, isArray, keyBy, last, max, min } from "lodash";
import driverLabel from "../../../utils/labels/driverLabel";
import tractorLabel from "../../../utils/labels/tractorLabel";
import trailerLabel from "../../../utils/labels/trailerLabel";
import { shipmentLocationAddressLabel } from "../../../utils/labels/shipmentLocationLabel";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import {
  Alert,
  Box,
  Button,
  Card,
  ClickAwayListener,
  Fade,
  IconButton,
  Popper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ShipmentInfosContainer from "../../shipment/ShipmentInfos";
import { LinkOutlined, PersonRemove, Schedule } from "@mui/icons-material";
import TripViolations from "../../trip/TripDetails/TripViolations";
import {
  formatDurationClock,
} from "../../../utils/labels/formatDateTime";
import DriverLicenseExpiryWarning from "../../asset-management/DriverLicenseExpiryWarning/DriverLicenseExpiryWarning";
import DriverMedicalExpiryWarning from "../../asset-management/DriverMedicalExpiryWarning/DriverMedicalExpiryWarning";
import { Feature, useFeature } from "../../account/Access/FeatureGuard";
import { DragEventArgs, EmitType, L10n } from "@syncfusion/ej2-base";

import { loadCldr } from "@syncfusion/ej2-base";
import { useTranslation } from "react-i18next";
import MaintenanceWarningsContainer from "../../maintenance/MaintenanceWarnings";
import QualificationWarningsContainer from "../../qualifications/QualificationWarnings";
import useDialog from "../../../utils/hooks/useDialog";
import TripSummary from "../../trip/TripSummary/TripSummary";
import TripRoute from "../TripPlanner/TripRoute";
import TripSeparationModal from "../../../views/trip-planning/TripSeparationModal";
import { useConfirm } from "material-ui-confirm";
import LocaleProvider from "../../../providers/LocaleProvider";
import { CalendarTripLiveEta } from "./CalendarTripLiveEta";
import { getSchedulerBarColor } from "./tripLiveEtaDisplay";
import { testIds } from "./PlanningCalendar.testIds";

loadCldr(
  require("cldr-data/supplemental/numberingSystems.json"),
  require("cldr-data/main/fr/ca-gregorian.json"),
  require("cldr-data/main/fr/numbers.json"),
  require("cldr-data/main/fr/timeZoneNames.json"),
);

L10n.load({
  en: {
    schedule: {
      today: "Today",
    },
    calendar: {
      today: "Today",
    },
  },
  fr: {
    schedule: {
      today: "Aujourd'hui",
    },
    calendar: {
      today: "Aujourd'hui",
    },
  },
});

export type PlanningCalendarProps = {
  drivers: PlanningToolProps["drivers"];
  tractors: PlanningToolProps["tractors"];
  trailers: PlanningToolProps["trailers"];
  driverShifts: PlanningToolProps["driverShifts"];
  maintenanceTasks: PlanningToolProps["maintenanceTasks"];
  onPeriodChange: PlanningToolProps["onPeriodChange"];
  shipments: PlanningToolProps["shipments"];
  filteredShipments: PlanningToolProps["filteredShipments"];
  onShipmentsChange: PlanningToolProps["onShipmentsChange"];
  onRefresh: PlanningToolProps["onRefresh"];
  selectedShipmentId: string | null;
  showDriveTimes?: boolean;
  parentHeight?: number;
  headerIndentTemplate?: () => JSX.Element;
  selectedDate?: Date;
  onShipmentsCombine: (shipments: AugmentedShipment[]) => void;
  onShipmentsUncombine: PlanningToolProps["onShipmentsUncombine"];
  onAssetLinkingOpen: (driverId: string) => void;
  onDriverShiftAssignmentOpen: (driverId: string) => void;
};

type DriverResource = {
  label: string;
  Id: string;
  color: string;
  subtitle: string;
  domicileId: string;
};

enum CalendarEventType {
  TRIP = "TRIP",
  PLACEHOLDER = "PLACEHOLDER",
}

type CalendarEvent = {
  Id: string;
  type: CalendarEventType;
  label: string;
  status: Status;
  pickupTime: string;
  dropoffTime: string;
  earliestPickup: Date;
  latestPickup: Date;
  description: string;
  driverId: string;
  shipmentId: string | null;
  shipmentNumber?: string;
  // The scheduler's pickup/dropoff times include the drive segments. Keep the
  // actual trip times alongside them so automation does not need to infer them.
  rawPickupTime?: string;
  rawDropoffTime?: string;
  plannedDwellSeconds?: number;
  // Drive time durations in seconds, included in the event's time span
  driveToPickupDuration?: number;
  driveToNextTripDuration?: number;
};

export const setAppointmentTripTimingAttributes = (
  element: HTMLElement | null,
  event: Pick<CalendarEvent, "type" | "pickupTime" | "dropoffTime"> &
    Partial<
      Pick<
        CalendarEvent,
        | "Id"
        | "shipmentId"
        | "shipmentNumber"
        | "driverId"
        | "rawPickupTime"
        | "rawDropoffTime"
        | "driveToPickupDuration"
        | "driveToNextTripDuration"
        | "plannedDwellSeconds"
      >
    >,
) => {
  if (!element) {
    return;
  }

  element.setAttribute("data-testid", testIds.planningCalendarAppointment);
  element.setAttribute("data-event-type", event.type);
  if (event.shipmentId) {
    element.setAttribute("data-shipment-id", event.shipmentId);
  }
  if (event.shipmentNumber) {
    element.setAttribute("data-shipment-number", event.shipmentNumber);
  }
  if (event.driverId) {
    element.setAttribute("data-driver-id", event.driverId);
  }

  if (event.type !== CalendarEventType.TRIP) {
    return;
  }

  element.setAttribute("data-trip-start", event.pickupTime);
  element.setAttribute("data-trip-end", event.dropoffTime);
  element.setAttribute("data-trip-render-start", event.pickupTime);
  element.setAttribute("data-trip-render-end", event.dropoffTime);

  if (event.rawPickupTime) {
    element.setAttribute("data-trip-pickup-start", event.rawPickupTime);
  }
  if (event.rawDropoffTime) {
    element.setAttribute("data-trip-dropoff-end", event.rawDropoffTime);
  }
  element.setAttribute(
    "data-drive-to-pickup-seconds",
    String(event.driveToPickupDuration ?? 0),
  );
  element.setAttribute(
    "data-drive-to-next-seconds",
    String(event.driveToNextTripDuration ?? 0),
  );
  element.setAttribute(
    "data-planned-dwell-total-seconds",
    String(event.plannedDwellSeconds ?? 0),
  );
};

export const getPlannedDwellSeconds = (
  shipment: Pick<AugmentedShipment, "trip">,
) =>
  shipment.trip?.shipmentLocations.reduce(
    (total, location) =>
      total +
      (location.setupDuration ?? 0) +
      (location.serviceDuration ?? 0),
    0,
  ) ?? 0;

export const getSchedulerViewAttributes = ({
  currentView,
  viewDateRange,
}: {
  currentView: View;
  viewDateRange: { start: Date; end: Date };
}) => ({
  "data-testid": testIds.planningCalendar,
  "data-view": currentView,
  "data-view-start": viewDateRange.start.toISOString(),
  "data-view-end": viewDateRange.end.toISOString(),
});

const PlanningCalendar = (props: PlanningCalendarProps) => {
  const { showDialog, hideDialog } = useDialog();
  const confirm = useConfirm();
  const { i18n, t } = useTranslation([
    "planning",
    "users",
    "orders",
    "common",
    "trips",
  ]);
  const [activeShipmentFromEvent, setActiveShipmentFromEvent] =
    useState<AugmentedShipment | null>(null);
  const { onShipmentsChange, shipments, onAssetLinkingOpen, onShipmentsUncombine } =
    props;
  const scheduleObjRef = useRef<ScheduleComponent>(null);
  const initialSyncDone = useRef<boolean>(false);

  const [currentView, setCurrentView] = useState<View>("TimelineDay");
  const [viewDateRange, setViewDateRange] = useState<{ start: Date; end: Date }>({
    start: startOfDay(new Date()),
    end: endOfDay(new Date()),
  });
  const viewDateRangeRef = useRef(viewDateRange);

  const [pxPerMinute, setPxPerMinute] = useState<number>(0);

  const computePxPerMinute = useCallback(() => {
    const cell = document.querySelector(
      ".schedule-drag-drop .e-work-cells",
    ) as HTMLElement | null;
    if (!cell) return;
    const timeScale = getTimeScaleForView(currentView);
    const slotDurationMinutes = timeScale.interval! / timeScale.slotCount!;
    const slotWidth = cell.getBoundingClientRect().width;
    if (slotWidth > 0) {
      setPxPerMinute(slotWidth / slotDurationMinutes);
    }
  }, [currentView]);

  useEffect(() => {
    // Cells may not exist yet on first render — retry after a short delay
    // to ensure Syncfusion has finished laying out the calendar.
    computePxPerMinute();
    const retryTimeout = setTimeout(computePxPerMinute, 500);
    const observer = new ResizeObserver(computePxPerMinute);
    const container = document.querySelector(".schedule-drag-drop");
    if (container) observer.observe(container);
    return () => {
      clearTimeout(retryTimeout);
      observer.disconnect();
    };
  }, [computePxPerMinute]);
  const { isAvailable: isCommodityDriven } = useFeature(
    Feature.CommodityManagement,
  );
  const orgSettingsQuery = useGetOrganizationSettingsQuery();
  const etaEnabled =
    !!orgSettingsQuery.data?.organizationSettings?.dispatching?.enableEta;

  const [selectedEvent, setSelectedEvent] =
    useState<CalendarEvent | null>(null);
  const [selectedEventElement, setSelectedEventElement] =
    useState<{
      getBoundingClientRect: () => DOMRect;
    } | null>(null);
  const [tripSeparationModalOpen, setTripSeparationModalOpen] = useState(false);

  const [hoveredShipment, setHoveredShipment] = useState<AugmentedShipment | null>(null);
  const [hoverAnchorEl, setHoverAnchorEl] = useState<Element | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const driverResourceData: DriverResource[] = useMemo(
    () =>
      props.drivers.map((driver) => ({
        label: driverLabel(driver),
        Id: driver._id,
        color: "#610010",
        subtitle: `${tractorLabel(driver.associatedTractor)} / ${trailerLabel(
          driver.associatedTrailer,
        )} ${driver.associatedAdditionalTrailers?.length ? " - " : ""} ${(
          driver.associatedAdditionalTrailers || []
        )
          .map(trailerLabel)
          .join(", ")}`,
        domicileId: driver.domicileId,
      })),
    [props.drivers],
  );

  // We need these refs because the resource header template
  // does not rerender with new props, when the props change
  const driverResourceDataRef = useRef(driverResourceData);
  const shipmentsRef = useRef(props.shipments);

  useEffect(() => {
    driverResourceDataRef.current = driverResourceData;
    const refreshLayoutTimeout = setTimeout(() => {
      scheduleObjRef.current?.refreshLayout();
    }, 250);

    return () => {
      clearTimeout(refreshLayoutTimeout);
    };
  }, [driverResourceData]);

  const driversById = useMemo(
    () =>
      keyBy<AugmentedDriver | null>(props.drivers, (driver) =>
        driver ? driver._id : "",
      ),
    [props.drivers],
  );

  useEffect(() => {
    shipmentsRef.current = props.shipments;
  }, [props.shipments]);

  const shipmentEvents: CalendarEvent[] = useMemo(
    () =>
      props.shipments
        .filter(
          (shipment) =>
            shipment.trip?.driver && shipment.trip?.status !== Status.Deleted,
        )
        .map((shipment) =>
          shipmentToEvents(
            shipment,
            isCommodityDriven,
            props.shipments,
            props.showDriveTimes ?? false,
          ),
        )
        .flat(),
    [props.shipments, props.showDriveTimes, isCommodityDriven],
  );

  const tryDropShipment = useCallback(
    (shipmentData: AugmentedShipment, targetCell: HTMLElement) => {
      if (!scheduleObjRef.current) {
        return;
      }

      if (targetCell.classList.contains("e-unavailable")) {
        return;
      }

      if (targetCell.classList.contains("e-drop-not-allowed")) {
        return;
      }
      if (targetCell.classList.contains("e-drop-would-conflict")) {
        return;
      }
      let cellData: CellClickEventArgs =
        scheduleObjRef.current.getCellDetails(targetCell);

      if (cellData?.groupIndex === undefined) {
        return;
      }

      let resourceDetails = scheduleObjRef.current.getResourcesByIndex(
        cellData.groupIndex,
      ) as ResourceDetails | undefined;

      if (!resourceDetails) {
        return;
      }

      const selectedPickupTime = new Date(cellData.startTime);

      const originalFirstPickupTime = new Date(
        shipmentData.trip?.firstPickupTime ||
          shipmentData.route?.firstPickupTime,
      );

      const timeShiftSeconds = differenceInSeconds(
        selectedPickupTime,
        originalFirstPickupTime,
      );

      const shiftedDropoffTime = addSeconds(
        new Date(
          shipmentData.trip?.lastDropoffTime ||
            shipmentData.route?.lastDropoffTime,
        ),
        timeShiftSeconds,
      );

      const driver = props.drivers.find(
        (driver) =>
          resourceDetails && driver._id === resourceDetails.resourceData.Id,
      );

      if (!driver) {
        return;
      }

      // Adjust shipment location arrival times by the same time shift
      const adjustedShipmentLocations = (
        shipmentData.trip?.shipmentLocations ||
        shipmentData.route?.locations ||
        []
      ).map((location) => ({
        ...location,
        arrivalTime: addSeconds(
          new Date(location.arrivalTime),
          timeShiftSeconds,
        ).toISOString(),
      }));

      onShipmentsChange(
        shipments.map((shipment) =>
          shipment._id === shipmentData._id
            ? {
                ...shipmentData,
                tripId: shipmentData.tripId || shipment._id,
                trip: {
                  _id: shipmentData.trip?._id || shipment._id,
                  tripNumber: shipmentData.trip?.tripNumber || "NEW",
                  status: shipmentData.trip?.status || Status.Assigned,
                  polyline: shipmentData.trip?.polyline || "",
                  driver: driver._id,
                  firstPickupTime: selectedPickupTime.toISOString(),
                  lastDropoffTime: shiftedDropoffTime.toISOString(),
                  tractor: driver.associatedTractor?._id,
                  trailer: driver.associatedTrailer?._id,
                  additionalTrailers: driver.associatedAdditionalTrailers?.map(
                    (trailer) => trailer._id,
                  ),
                  routeDistance:
                    shipmentData.trip?.routeDistance ||
                    shipmentData.route?.routeDistance ||
                    0,
                  shipmentLocations: adjustedShipmentLocations,
                  distanceToStart: shipmentData.trip?.distanceToStart ?? 0,
                  distanceToEnd: shipmentData.trip?.distanceToEnd ?? 0,
                  etaFromPreviousTrip: 0,
                  etaToNextTrip: 0,
                  violations: [],
                },
                previousPickupTime: shipment.trip?.firstPickupTime,
              }
            : {
                ...shipment,
                previousPickupTime: shipment.trip?.firstPickupTime,
              },
        ),
        driver._id,
      );

      return true;
    },
    [onShipmentsChange, props.drivers, shipments],
  );

  const onShipmentDrop = (event: React.DragEvent<HTMLDivElement>) => {
    const targetCell = event.target as HTMLElement;
    if (!targetCell) {
      return;
    }

    const shipmentDataString = event.dataTransfer.getData("text/plain");

    const shipmentData = JSON.parse(shipmentDataString) as AugmentedShipment;

    // Combine Shipments
    const eventElement = targetCell.closest(".e-appointment");
    if (eventElement) {
      const shipmentEvent =
        scheduleObjRef.current?.getEventDetails(eventElement);
      const shipment = props.shipments.find(
        (shipment) => shipment._id === shipmentEvent?.Id,
      );
      if (!shipment) {
        return;
      }
      const allShipments = (shipment?.otherShipmentsInTrip || []).concat(
        shipment,
      );
      showDialog({
        title: "Confirmation",
        description: "Do you want to combine these shipments?",
        type: "primary",
        actions: [
          {
            title: "Combine",
            type: "primary",
            onClick: () => {
              hideDialog();
              props.onShipmentsCombine(allShipments.concat(shipmentData));
            },
          },
          {
            title: "Cancel",
            type: "secondary",
            onClick: hideDialog,
          },
        ],
      });
      return;
    }
    if (!targetCell.classList.contains("e-work-cells")) {
      return;
    }

    tryDropShipment(shipmentData, targetCell);
  };

  const highlightWorkHours = useCallback(() => {
    const workCells = document.querySelectorAll(".e-work-cells");
    workCells.forEach((node) => {
      // check if node is a td element
      if (!(node instanceof HTMLElement)) {
        return;
      }
      if (!scheduleObjRef.current) {
        return;
      }
      const element = node;
      const cellStartDate = getCellStartTime(element);
      const cellEndDate = getCellEndTime(element);

      const groupIndex = Number(element.getAttribute("data-group-index"));
      const resourceDetails = scheduleObjRef.current.getResourcesByIndex(
        groupIndex,
      ) as ResourceDetails | undefined;

      if (!resourceDetails) {
        return;
      }
      const driverId = resourceDetails.resourceData.Id;
      const domicileId = resourceDetails.resourceData.domicileId;

      const driver = props.drivers.find((driver) => driver._id === driverId);

      const maintenanceTaskAppliesToDriver = (
        task: PlanningToolProps["maintenanceTasks"][0],
      ) => {
        if (task.assetType === TripAssetTypes.Driver) {
          return task.assetId === driverId;
        }
        if (task.assetType === TripAssetTypes.Tractor) {
          return driver?.associatedTractor?._id === task.assetId;
        }
        if (task.assetType === TripAssetTypes.Trailer) {
          return driver?.associatedTrailer?._id === task.assetId;
        }
      };

      const hasNoShift =
        !driver?.driverShiftAssignment &&
        props.driverShifts.every(
          (shift) => !shiftAppliesToDriver(shift, driverId, domicileId),
        );

      const hasNoMaintanceTask = props.maintenanceTasks.every(
        (task) => !maintenanceTaskAppliesToDriver(task),
      );

      const fromDomicileTz = (date: Date, domicileTimeZone?: string) => {
        return zonedTimeToUtc(date, domicileTimeZone || "UTC");
      };

      const toDomicileTz = (date: Date, domicileTimeZone?: string) => {
        return utcToZonedTime(date, domicileTimeZone || "UTC");
      };

      const licenceOrMedicalHasExpiredInCell =
        (!!driver?.license?.expiryDate &&
          new Date(driver.license.expiryDate) <= cellStartDate) ||
        (!!driver?.medical?.expiryDate &&
          new Date(driver.medical.expiryDate) <= cellStartDate);

      if (licenceOrMedicalHasExpiredInCell) {
        element.classList.add("e-license-expired");
        element.classList.add("e-medical-expired");
      }

      const maintenanceTaskInCell = props.maintenanceTasks.some((task) => {
        const taskApplies = maintenanceTaskAppliesToDriver(task);
        if (!taskApplies) {
          return false;
        }
        if (task.startDate) {
          const taskStartTime = new Date(task.startDate);
          const taskEndTime = task.endDate ? new Date(task.endDate) : null;

          if (
            taskStartTime <= cellStartDate &&
            (!taskEndTime || taskEndTime >= cellEndDate)
          ) {
            return true;
          } else {
            return false;
          }
        } else if (task.startMileage) {
          const assetType = task.assetType;
          if (assetType === TripAssetTypes.Tractor) {
            return (
              task.startMileage <= (driver?.associatedTractor?.mileage || 0)
            );
          } else if (assetType === TripAssetTypes.Trailer) {
            return (
              task.startMileage <= (driver?.associatedTrailer?.mileage || 0)
            );
          }
        }

        return false;
      });

      const isWorkingInCell =
        !licenceOrMedicalHasExpiredInCell &&
        (hasNoShift ||
          props.driverShifts.some((shift) => {
            const shiftApplies = driver?.driverShiftAssignment
              ? driver.driverShiftAssignment.driverShiftId === shift._id
              : shiftAppliesToDriver(shift, driverId, domicileId);

            if (!shiftApplies) {
              return false;
            }
            // Use shift's domicile timezone if available, otherwise use browser timezone
            const domicileTz =
              shift.domicile?.addressTimezone ||
              driver?.domicileEntity?.addressTimezone ||
              LocaleProvider.getTimezone();

            const cellStartDateAtDomicile = toDomicileTz(
              cellStartDate,
              domicileTz,
            );
            const cellEndDateAtDomicile = toDomicileTz(cellEndDate, domicileTz);

            const shiftIncludesCell = shift.days.some((day) => {
              let shiftStartTime = new Date(cellStartDateAtDomicile);
              shiftStartTime = setDay(shiftStartTime, day);
              shiftStartTime.setHours(
                shift.startTime.hour || 0,
                shift.startTime.minute || 0,
              );
              let shiftEndTime = addSeconds(
                new Date(cellEndDateAtDomicile),
                -1,
              );
              shiftEndTime = setDay(shiftEndTime, day);
              shiftEndTime.setHours(
                shift.endTime.hour || 0,
                shift.endTime.minute || 0,
              );

              if (isBefore(shiftEndTime, shiftStartTime)) {
                shiftEndTime = addDays(shiftEndTime, 1);
              }
              shiftStartTime = fromDomicileTz(shiftStartTime, domicileTz);
              shiftEndTime = fromDomicileTz(shiftEndTime, domicileTz);

              if (
                shiftStartTime <= cellStartDate &&
                shiftEndTime >= cellEndDate
              ) {
                return true;
              } else {
                return false;
              }
            });

            return shiftIncludesCell;
          })) &&
        (hasNoMaintanceTask || !maintenanceTaskInCell);

      if (isWorkingInCell) {
        element.classList.remove("e-unavailable");
      } else {
        element.classList.add("e-unavailable");
        if (licenceOrMedicalHasExpiredInCell) {
          element.classList.add("e-license-expired");
          element.classList.add("e-medical-expired");
        }
      }
    });
  }, [
    props.driverShifts,
    props.maintenanceTasks,
    scheduleObjRef,
    props.drivers,
  ]);

  const highlightDropIndicators = useCallback(
    (shipmentId: string | null) => {
      const workCells = document.querySelectorAll(".e-work-cells");
      if (!shipmentId?.length) {
        workCells.forEach((node) => {
          node.classList.remove("e-drop-allowed");
          node.classList.remove("e-drop-not-allowed");
          node.classList.remove("e-drop-would-conflict");
        });
        const combinableEvents = document.querySelectorAll(".e-combinable");
        combinableEvents.forEach((node) => {
          node.classList.remove("e-combinable");
        });
      }
      const selectedShipment = props.shipments.find(
        (shipment) => shipment._id === shipmentId,
      );
      if (!selectedShipment) {
        return;
      }
      const selectedShipmentEvent = shipmentToEvents(
        selectedShipment,
        isCommodityDriven,
        props.shipments,
        props.showDriveTimes ?? false,
      ).find((event) => event.Id === shipmentId);

      if (!selectedShipmentEvent) {
        return;
      }

      if (!scheduleObjRef.current) {
        return;
      }
      const currentViewEvents = (
        scheduleObjRef.current.getCurrentViewEvents() as CalendarEvent[]
      ).filter((e) => e.type === CalendarEventType.TRIP);
      const combinableEvents = currentViewEvents.filter((event) => {
        const shipmentForEvent = props.shipments.find(
          (shipment) => shipment._id === event.Id,
        );
        if (!shipmentForEvent) {
          return false;
        }
        if (shipmentForEvent.loadType === LoadType.FullTruckLoad) {
          return false;
        }
        if (selectedShipment.loadType === LoadType.FullTruckLoad) {
          return false;
        }
        const shipment1 = selectedShipment;
        const shipment2 = shipmentForEvent;
        const minTimeWindowDate1 = min(
          shipment1.shipmentLocations.flatMap((loc) =>
            loc.timeWindows.map((tw) => tw.fromDate),
          ),
        );
        const maxTimeWindowDate1 = max(
          shipment1.shipmentLocations.flatMap((loc) =>
            loc.timeWindows.map((tw) => tw.toDate),
          ),
        );
        const minTimeWindowDate2 = min(
          shipment2.shipmentLocations.flatMap((loc) =>
            loc.timeWindows.map((tw) => tw.fromDate),
          ),
        );
        const maxTimeWindowDate2 = max(
          shipment2.shipmentLocations.flatMap((loc) =>
            loc.timeWindows.map((tw) => tw.toDate),
          ),
        );

        return (
          (minTimeWindowDate1 <= maxTimeWindowDate2 &&
            minTimeWindowDate1 >= minTimeWindowDate2) ||
          (maxTimeWindowDate1 >= minTimeWindowDate2 &&
            maxTimeWindowDate1 <= maxTimeWindowDate2) ||
          (minTimeWindowDate2 <= maxTimeWindowDate1 &&
            minTimeWindowDate2 >= minTimeWindowDate1) ||
          (maxTimeWindowDate2 >= minTimeWindowDate1 &&
            maxTimeWindowDate2 <= maxTimeWindowDate1)
        );
      });

      combinableEvents.forEach((event) => {
        const element = document.querySelector(
          '[data-id="Appointment_' + event.Id + '"]',
        );
        if (!element) {
          return;
        }
        element.classList.add("e-combinable");
      });
      workCells.forEach((node) => {
        // check if node is a td element
        if (!(node instanceof HTMLElement)) {
          return;
        }
        if (!scheduleObjRef.current) {
          return;
        }
        const element = node;

        const groupIndex = getCellGroupIndex(element);
        const cellStartTime = getCellStartTime(element);
        const cellEndTime = getCellEndTime(element);
        const resourceDetails = scheduleObjRef.current.getResourcesByIndex(
          groupIndex,
        ) as ResourceDetails | undefined;
        const resourceData =
          resourceDetails?.resourceData as DriverResource | null;
        const isSlotAvailable = !currentViewEvents.some(
          (event) =>
            event.Id !== shipmentId &&
            event.driverId === resourceData?.Id &&
            new Date(event.pickupTime) < cellEndTime &&
            new Date(event.dropoffTime) > cellStartTime,
        );

        if (!isSlotAvailable) {
          element.classList.add("e-drop-not-allowed");
          return;
        }
        const cellStartDate = cellStartTime;

        if (!resourceDetails) {
          return;
        }
        const driverId = resourceDetails.resourceData.Id;

        const driver = props.drivers.find((driver) => driver._id === driverId);

        if (
          driver?.associatedTrailer?.type &&
          ![
            selectedShipment.trailerType,
            ...(selectedShipment.additionalTrailerTypes || []),
          ].includes(driver.associatedTrailer.type)
        ) {
          element.classList.add("e-drop-not-allowed");
          return;
        }

        const earliestPickup = new Date(selectedShipmentEvent.earliestPickup);
        const latestPickup = new Date(selectedShipmentEvent.latestPickup);
        if (earliestPickup <= cellStartDate && latestPickup >= cellStartDate) {
          element.classList.add("e-drop-allowed");
        } else {
          element.classList.add("e-drop-not-allowed");
        }
      });

      workCells.forEach((node) => {
        // check if node is a td element
        if (!(node instanceof HTMLElement)) {
          return;
        }
        if (!scheduleObjRef.current) {
          return;
        }
        const element = node;
        if (element.classList.contains("e-drop-not-allowed")) {
          return;
        }

        const projectedStartTime = getCellStartTime(element);

        const projectedEndTime = addSeconds(
          projectedStartTime,
          differenceInSeconds(
            new Date(selectedShipmentEvent.dropoffTime),
            new Date(selectedShipmentEvent.pickupTime),
          ),
        );
        const groupIndex = getCellGroupIndex(element);
        const resourceDetails = scheduleObjRef.current.getResourcesByIndex(
          groupIndex,
        ) as ResourceDetails | undefined;
        const resourceData =
          resourceDetails?.resourceData as DriverResource | null;

        const resourceWorkCells = Array.from(workCells).filter((cell) => {
          return getCellGroupIndex(cell) === groupIndex;
        });

        const projectedOccupiedCells = Array.from(resourceWorkCells).filter(
          (cell) => {
            const cellStartTime = getCellStartTime(cell);
            const cellEndTime = getCellEndTime(cell);
            if (
              (projectedStartTime <= cellStartTime &&
                cellStartTime <= projectedEndTime) ||
              (projectedStartTime < cellEndTime &&
                cellEndTime <= projectedEndTime)
            ) {
              return true;
            }

            return false;
          },
        );

        const projectedOccupiedCellsAreUnavailable =
          projectedOccupiedCells.some((projectedOccupiedCell) => {
            if (!scheduleObjRef.current) {
              return false;
            }
            if (projectedOccupiedCell.classList.contains("e-unavailable")) {
              return true;
            }
            const projectedOccupiedCellStartTime = getCellStartTime(
              projectedOccupiedCell,
            );
            const projectedOccupiedCellEndTime = getCellEndTime(
              projectedOccupiedCell,
            );
            return currentViewEvents.some(
              (event) =>
                event.Id !== shipmentId &&
                event.driverId === resourceData?.Id &&
                new Date(event.pickupTime) < projectedOccupiedCellEndTime &&
                new Date(event.dropoffTime) > projectedOccupiedCellStartTime,
            );
          });

        if (projectedOccupiedCellsAreUnavailable) {
          element.classList.remove("e-drop-allowed");
          element.classList.add("e-drop-would-conflict");
          return;
        }
      });
    },
    [props.drivers, props.shipments, props.showDriveTimes, isCommodityDriven],
  );

  const sync = useCallback(() => {
    setTimeout(() => {
      if (scheduleObjRef.current) {
        const lastDate = last(scheduleObjRef.current.getCurrentViewDates());
        if (!lastDate) {
          return;
        }
        const start = startOfDay(scheduleObjRef.current.getCurrentViewDates()[0]);
        const end = endOfDay(lastDate);
        // dataBound runs sync on every refreshEvents. onPeriodChange rebuilds
        // the drivers and event settings, which schedules another refreshEvents.
        // Notifying the parent only when the visible range changed is what
        // stops that loop. Do not call onPeriodChange unconditionally.
        const didViewRangeChange =
          viewDateRangeRef.current.start.getTime() !== start.getTime() ||
          viewDateRangeRef.current.end.getTime() !== end.getTime();
        if (didViewRangeChange) {
          viewDateRangeRef.current = { start, end };
          props.onPeriodChange({ start, end });
          setViewDateRange({ start, end });
        }
      }
      highlightWorkHours();
      computePxPerMinute();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleObjRef, highlightWorkHours, computePxPerMinute]);

  useEffect(() => {
    setTimeout(() => {
      highlightWorkHours();
    }, 250);
  }, [props, highlightWorkHours]);

  useEffect(() => {
    if (shipmentEvents.length && !initialSyncDone.current) {
      sync();
      initialSyncDone.current = true;
    }
  }, [sync, initialSyncDone, shipmentEvents]);

  useEffect(() => {
    highlightWorkHours();
  }, [shipmentEvents, highlightWorkHours]);

  useEffect(
    function highlightDropIndicatorsOnSelectedShipmentChange() {
      highlightDropIndicators(props.selectedShipmentId);
    },
    [props.selectedShipmentId, highlightDropIndicators],
  );

  // We need these refs because the resource header template
  // does not rerender with new props, when the props change
  const onAssetLinkingOpenRef = useRef(onAssetLinkingOpen);

  useEffect(() => {
    onAssetLinkingOpenRef.current = onAssetLinkingOpen;
  }, [onAssetLinkingOpen]);

  const onDriverShiftAssignmentOpenRef = useRef(
    props.onDriverShiftAssignmentOpen,
  );

  useEffect(() => {
    onDriverShiftAssignmentOpenRef.current = props.onDriverShiftAssignmentOpen;
  }, [props.onDriverShiftAssignmentOpen]);

  const onEventRendered = useCallback(
    (args: EventRenderedArgs) => {
      const element = args.element as HTMLElement;
      const event = args.data as CalendarEvent;
      setAppointmentTripTimingAttributes(element, event);

      if (!element || element.dataset.hoverBound) return;
      element.dataset.hoverBound = "1";

      element.addEventListener("mouseenter", () => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
          const eventId = element
            .getAttribute("data-id")
            ?.replace("Appointment_", "");
          const shipment = props.shipments.find((s) => s._id === eventId);
          if (shipment) {
            setHoveredShipment(shipment);
            setHoverAnchorEl(element);
          }
        }, 2000);
      });

      element.addEventListener("mouseleave", () => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        setHoveredShipment(null);
        setHoverAnchorEl(null);
      });
    },
    [props.shipments],
  );

  const resourceHeaderTemplate = useCallback(
    function ({
      resourceData,
    }: {
      resourceData: DriverResource;
    }): JSX.Element | null {
      const driverResource = driverResourceDataRef.current.find(
        (resource) => resource.Id === resourceData.Id,
      );
      if (!driverResource) {
        return null;
      }
      const driver = driversById[driverResource.Id];

      return (
        <Box
          data-testid={testIds.planningCalendarResourceRow}
          data-driver-id={driverResource.Id}
          sx={{
            position: "relative",
            height: 60,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              width: "100%",
              height: 60,
            }}
          >
            <Stack
              direction="column"
              sx={{
                flex: 1,
                width: "calc(100%-30px)",
                marginLeft: "0 !important",
              }}
            >
              <Typography
                data-testid={testIds.planningCalendarDriverName(
                  driverResource.Id,
                )}
                variant="body1"
                sx={{
                  wordBreak: "break-word",
                  whiteSpace: "nowrap",
                }}
              >
                <Tooltip
                  title={t("driver.assignShift", "Assign driver to a shift")}
                >
                  <IconButton data-testid={testIds.planningCalendarAssignDriverToShift}
                    onClick={() => {
                      onDriverShiftAssignmentOpenRef.current(driverResource.Id);
                    }}
                    color="primary"
                    size="small"
                    sx={{
                      padding: 0.2,
                    }}
                  >
                    <Schedule />
                  </IconButton>
                </Tooltip>
                {driverResource.label}
              </Typography>
              <Stack direction="row" alignItems="center">
                <Typography variant="caption">
                  <Tooltip
                    title={t("driver.linkToAsset", "Link to tractor/trailer")}
                  >
                    <IconButton data-testid={testIds.planningCalendarLinkDriverToAsset}
                      onClick={() => {
                        onAssetLinkingOpenRef.current(driverResource.Id);
                      }}
                      color="primary"
                      size="small"
                      sx={{
                        padding: 0.2,
                      }}
                    >
                      <LinkOutlined />
                    </IconButton>
                  </Tooltip>
                  {driverResource.subtitle}
                </Typography>
                <Box
                  sx={{
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {driver ? (
                    <DriverLicenseExpiryWarning driver={driver} />
                  ) : null}
                  {driver ? (
                    <DriverMedicalExpiryWarning driver={driver} />
                  ) : null}
                  {driver ? (
                    <MaintenanceWarningsContainer
                      assetType={TripAssetTypes.Driver}
                      assetId={driver._id}
                    />
                  ) : null}
                  {driver ? (
                    <QualificationWarningsContainer
                      assetType={TripAssetTypes.Driver}
                      assetId={driver._id}
                    />
                  ) : null}
                  {driver?.associatedTractor ? (
                    <MaintenanceWarningsContainer
                      assetType={TripAssetTypes.Tractor}
                      assetId={driver.associatedTractor._id}
                    />
                  ) : null}
                  {driver?.associatedTrailer ? (
                    <MaintenanceWarningsContainer
                      assetType={TripAssetTypes.Trailer}
                      assetId={driver.associatedTrailer._id}
                    />
                  ) : null}
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      );
    },
    [driversById, t],
  );

  const quickInfoContentTemplate = useCallback(
    function (event: CalendarEvent): JSX.Element | null {
      const shipment = shipmentsRef.current.find(
        (shipment) => shipment._id === event.shipmentId,
      );
      if (!shipment) {
        return (
          <Box>
            <Typography variant="body2">{event.description}</Typography>
          </Box>
        );
      }
      return (
        <Box
          sx={{
            p: 2,
            backgroundColor: "primaryBackground.main",
          }}
        >
          {shipment.trip ? (
            <TripViolations
              trip={shipment.trip}
              shiftEndWarningTestId={
                testIds.planningCalendarTripAfterShiftWarning
              }
            />
          ) : null}
          {shipment.trip ? <CalendarTripSummary shipment={shipment} /> : null}
          {shipment.trip ? <CalendarTripLiveEta trip={shipment.trip} /> : null}
          {props.showDriveTimes && shipment.trip?.etaToNextTrip ? (
            <Alert severity="info">
              <Typography variant="caption">
                {t("etaToNextTrip")}{" "}
                {formatDurationClock(shipment.trip.etaToNextTrip)}
              </Typography>
            </Alert>
          ) : null}
          {shipment.trip?.status !== Status.Complete ? (
            <Tooltip title={t("removeDriverFromTrip")}>
              <IconButton data-testid={testIds.planningCalendarRemoveDriverFromTrip}
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                }}
                onClick={() => {
                  showDialog({
                    title: t("removeDriverFromTrip", "Remove driver from trip"),
                    description: (
                      <>
                        <Typography>
                          {t(
                            "unassignConfirm",
                            "Do you really want to remove this driver from the trip? This action cannot be undone.",
                          )}
                        </Typography>
                        {shipment.status === Status.InProgress && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            {t(
                              "unassignInProgressConfirm",
                              "This trip is in progress. Removing the driver will return it to waiting for assignment. Its event history and completed stops will remain visible.",
                            )}
                          </Alert>
                        )}
                        <Box sx={{ display: "flex", flexDirection: "row" }}>
                          <Typography>
                            {capitalize(t("users:driver.one", "Driver"))}:&nbsp;
                          </Typography>
                          <Typography fontWeight={"bold"}>
                            {driverResourceDataRef.current.find(
                              (d) => d.Id === shipment.trip?.driver,
                            )?.label || ""}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "row" }}>
                          <Typography>
                            {t("orders:trip", "Trip")} #:&nbsp;
                          </Typography>
                          <Typography fontWeight={"bold"}>
                            {shipment?.trip?.tripNumber}
                          </Typography>
                        </Box>
                        <br />
                        {/* This is temporary for now, waiting for real reasons. */}
                        {/* <ReasonForm
                          title="Reason:"
                          reasons={[
                            {
                              label: "Driver unable to finish the trip",
                              value: "driver-unable-to-finish-trip",
                            },
                            {
                              label: "Request from mamagement",
                              value: "request-from-management",
                            },
                            {
                              label: "Other",
                              value: "other",
                            },
                          ]}
                          onChange={() => {}}
                        /> */}
                      </>
                    ),
                    actions: [
                      {
                        title: t("common:error.yesRemove", "Yes, remove"),
                        type: "error",
                        onClick: () => {
                          onShipmentsChange(
                            shipmentsRef.current.map((shipment) =>
                              shipment._id === event.Id && shipment.trip
                                ? {
                                    ...shipment,
                                    trip: {
                                      ...shipment.trip,
                                      driver: null,
                                      tractor: null,
                                      trailer: null,
                                      additionalTrailers: null,
                                    },
                                  }
                                : shipment,
                            ),
                          );
                          setSelectedEvent(null);
                          setSelectedEventElement(null);
                          hideDialog();
                        },
                      },
                      {
                        title: t("common:cancel", "Cancel"),
                        type: "primary",
                      },
                    ],
                  });
                }}
              >
                <PersonRemove />
              </IconButton>
            </Tooltip>
          ) : null}
          {shipment.otherShipmentsInTrip.length > 0 && (
            <Stack direction="row" justifyContent="center">
              <Button data-testid={testIds.planningCalendarSeparateTrip}
                sx={{ alignSelf: "center" }}
                onClick={() => {
                  setSelectedEvent(null);
                  setSelectedEventElement(null);
                  if (
                    [shipment, ...shipment.otherShipmentsInTrip].some(
                      (s) =>
                        s.status === Status.Complete ||
                        s.status === Status.InProgress,
                    )
                  ) {
                    confirm({
                      title: t("common:error.title", "Error"),
                      description: t(
                        "trips:error.unseparatableTrips",
                        "Cannot separate a trip with completed or in progress shipments",
                      ),
                      hideCancelButton: true,
                      titleProps: { color: "error" },
                    });
                    return;
                  }
                  setTripSeparationModalOpen(true);
                }}
              >
                {t("trips:separateTrip", "Separate Trip")}
              </Button>
            </Stack>
          )}
        </Box>
      );
    },
    [onShipmentsChange, props.showDriveTimes, t, showDialog, hideDialog, confirm],
  );

  const hoverQuickInfoContent = useCallback(
    function (event: CalendarEvent): JSX.Element | null {
      const shipment = shipmentsRef.current.find(
        (shipment) => shipment._id === event.shipmentId,
      );
      if (!shipment) {
        return (
          <Box>
            <Typography variant="body2">{event.description}</Typography>
          </Box>
        );
      }
      return (
        <Box
          sx={{
            p: 2,
            backgroundColor: "primaryBackground.main",
            pointerEvents: "none",
          }}
        >
          {shipment.trip ? (
            <TripViolations
              trip={shipment.trip}
              shiftEndWarningTestId={
                testIds.planningCalendarTripAfterShiftWarning
              }
            />
          ) : null}
          {shipment.trip ? <CalendarTripSummary shipment={shipment} /> : null}
          {shipment.trip ? <CalendarTripLiveEta trip={shipment.trip} /> : null}
          {props.showDriveTimes && shipment.trip?.etaToNextTrip ? (
            <Alert severity="info">
              <Typography variant="caption">
                {t("etaToNextTrip")}{" "}
                {formatDurationClock(shipment.trip.etaToNextTrip)}
              </Typography>
            </Alert>
          ) : null}
        </Box>
      );
    },
    [props.showDriveTimes, t],
  );

  const quickInfoTemplates = useMemo(
    () =>
      // @ts-ignore
      ({
        content: quickInfoContentTemplate,
        templateType: "Event",
        header: () => null,
        footer: () => null,
      } as QuickInfoTemplatesModel),
    [quickInfoContentTemplate],
  );

  const editorTemplate = useCallback(function (
    event: CalendarEvent,
  ): JSX.Element | null {
    // We use the ref here so the editorTemplate callback does not
    // change and trigger a rerender of the scheduler when shipments change
    // When that happens, the scheduler calls the editorTemplate
    // with an empty event due to the array reference changing
    const shipment = shipmentsRef.current.find(
      (shipment) => shipment._id === event.shipmentId,
    );

    if (!shipment) {
      return null;
    }

    return shipment.trip ? (
      <CalendarTripDetails
        shipment={shipment}
        // onRefresh={(shipmentIds) => {
        //   scheduleObjRef.current?.closeEditor();
        //   return onRefresh(shipmentIds);
        // }}
      />
    ) : null;
  },
  []);

  const group = useMemo(
    () => ({
      enableCompactView: false,
      resources: ["Consultants"],
    }),
    [],
  );

  const timeScale = useMemo(
    () => getTimeScaleForView(currentView),
    [currentView],
  );

  const theme = useTheme();

  const eventTemplate = useCallback(
    (event: CalendarEvent) => {
      if (event.type !== CalendarEventType.TRIP) {
        return null;
      }

      const shipment = props.shipments.find((s) => s._id === event.Id);
      const hasViolations = !!shipment?.trip?.violations?.length;
      const tripColor = getSchedulerBarColor(
        etaEnabled ? shipment?.trip?.etaStatus : null,
        hasViolations,
        event.status,
        theme.palette,
      );

      // Clamp drive segments to the visible view range at display time.
      // The raw durations are preserved on the event for accurate tooltip labels.
      const tripPickupTime = new Date(event.pickupTime).getTime() + (event.driveToPickupDuration ?? 0) * 1000;
      const tripDropoffTime = new Date(event.dropoffTime).getTime() - (event.driveToNextTripDuration ?? 0) * 1000;

      const drivePickupStart = tripPickupTime - (event.driveToPickupDuration ?? 0) * 1000;
      const visibleDrivePickupStart = Math.max(drivePickupStart, viewDateRange.start.getTime());
      const visibleDrivePickupMinutes = Math.max((tripPickupTime - visibleDrivePickupStart) / 60000, 0);

      const driveNextEnd = tripDropoffTime + (event.driveToNextTripDuration ?? 0) * 1000;
      const visibleDriveNextEnd = Math.min(driveNextEnd, viewDateRange.end.getTime());
      const visibleDriveNextMinutes = Math.max((visibleDriveNextEnd - tripDropoffTime) / 60000, 0);

      // Absolute pixel widths based on the schedule's time scale — accurate
      // regardless of view type or whether the event spans multiple days.
      const driveToPickupPx = pxPerMinute > 0 ? visibleDrivePickupMinutes * pxPerMinute : 0;
      const driveToNextPx = pxPerMinute > 0 ? visibleDriveNextMinutes * pxPerMinute : 0;

      return (
        <div
          data-shipment-id={event.shipmentId ?? undefined}
          data-shipment-number={event.shipmentNumber}
          data-event-type={event.type}
          style={{
            display: "flex",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            borderRadius: 3,
          }}
        >
          {driveToPickupPx > 0 && (
            <Tooltip
              title={`⛟ ${formatDurationClock(event.driveToPickupDuration!)} drive to pickup`}
            >
              <div
                data-testid={testIds.planningCalendarDriveToPickup}
                data-drive-segment="to-pickup"
                data-shipment-id={event.shipmentId ?? undefined}
                data-duration-seconds={event.driveToPickupDuration}
                style={{
                  width: driveToPickupPx,
                  minWidth: driveToPickupPx,
                  backgroundColor: "#3f51b5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: "#fff", px: 0.5, fontSize: "0.65rem" }}
                >
                  ⛟ {formatDurationClock(event.driveToPickupDuration!)}
                </Typography>
              </div>
            </Tooltip>
          )}
          <div
            className="e-appointment-trip-body"
            style={{
              flex: 1,
              minWidth: 0,
              backgroundColor: tripColor,
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              padding: "0 4px",
            }}
          >
            <Typography
              variant="caption"
              noWrap
              sx={{ color: "#fff", fontWeight: 500, fontSize: "0.7rem" }}
            >
              {event.label}
            </Typography>
          </div>
          {driveToNextPx > 0 && (
            <Tooltip
              title={`⛟ ${formatDurationClock(event.driveToNextTripDuration!)} drive to next trip`}
            >
              <div
                data-testid={testIds.planningCalendarDriveToNextTrip}
                data-drive-segment="to-next-trip"
                data-shipment-id={event.shipmentId ?? undefined}
                data-duration-seconds={event.driveToNextTripDuration}
                style={{
                  width: driveToNextPx,
                  minWidth: driveToNextPx,
                  backgroundColor: "#6c3fb5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: "#fff", px: 0.5, fontSize: "0.65rem" }}
                >
                  ⛟ {formatDurationClock(event.driveToNextTripDuration!)}
                </Typography>
              </div>
            </Tooltip>
          )}
        </div>
      );
    },
    [props.shipments, theme.palette, pxPerMinute, viewDateRange, etaEnabled],
  );

  const eventSettings: EventSettingsModel = useMemo(
    () => ({
      dataSource: shipmentEvents
        // We need placeholders for drivers with no shipments
        // otherwise they won't display in the resources
        .concat(
          driverResourceData.map((driverResource) => ({
            description: "",
            driverId: driverResource.Id,
            Id: `driver-${driverResource.Id}-placeholder-event`,
            label: "",
            status: Status.Assigned,
            pickupTime: new Date(0).toISOString(),
            dropoffTime: new Date(0).toISOString(),
            earliestPickup: new Date(0),
            latestPickup: new Date(0),
            type: CalendarEventType.PLACEHOLDER,
            shipmentId: null,
          })),
        ),
      fields: {
        id: "Id",
        subject: { title: "Order #", name: "label" },
        startTime: { title: "Pickup time", name: "pickupTime" },
        endTime: { title: "Dropoff time", name: "dropoffTime" },
        description: { title: "Description", name: "description" },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      template: eventTemplate as any,
    }),
    [shipmentEvents, driverResourceData, eventTemplate],
  );

  const cellDoubleClick: EmitType<CellClickEventArgs> = useCallback((event) => {
    event.cancel = true;
  }, []);

  const cellClick: EmitType<CellClickEventArgs> = useCallback((event) => {
    event.cancel = true;
  }, []);

  const navigating: EmitType<NavigatingEventArgs> = useCallback(
    (event) => {
      if (event.action === "view") {
        setCurrentView(event.currentView);
        if (scheduleObjRef.current) {
          scheduleObjRef.current.timeScale = getTimeScaleForView(
            event.currentView,
          );
        }
      }
      sync();
    },
    [sync],
  );

  const drag: EmitType<DragEventArgs> = useCallback(
    (payload: { data: CalendarEvent }) => {
      highlightDropIndicators(payload.data.Id);
    },
    [highlightDropIndicators],
  );

  const dragStart = useCallback(
    (payload: { data: CalendarEvent; cancel: boolean }) => {
      if (payload.data.type !== CalendarEventType.TRIP) {
        payload.cancel = true;
      }
    },
    [],
  );

  const dragStop: EmitType<DragEventArgs> = useCallback(
    (payload: { cancel: boolean; data: CalendarEvent; event: Event }) => {
      const { data } = payload;
      if (data.status === Status.Complete) {
        payload.cancel = true;
      }
      const target = payload.event.target as HTMLElement;
      if (!target) {
        return;
      }
      const shipmentData = props.shipments.find(
        (shipment) => shipment._id === data.Id,
      );
      if (!shipmentData) {
        return;
      }
      const isAccepted = tryDropShipment(shipmentData, target);
      if (!isAccepted) {
        payload.cancel = true;
      }
      highlightDropIndicators(null);
    },
    [props.shipments, tryDropShipment, highlightDropIndicators],
  );

  useEffect(() => {
    if (scheduleObjRef.current && props.parentHeight) {
      scheduleObjRef.current.refresh();
    }
  }, [props.parentHeight]);

  // Somehow juste updating the eventSettings prop does not trigger a rerender
  useEffect(() => {
    const refreshEventsTimeout = setTimeout(() => {
      if (scheduleObjRef.current) {
        scheduleObjRef.current.eventSettings = eventSettings;
        scheduleObjRef.current.refreshEvents();
      }
    }, 250);

    return () => {
      clearTimeout(refreshEventsTimeout);
    };
  }, [eventSettings]);

  return (
    <div
      {...getSchedulerViewAttributes({
        currentView,
        viewDateRange,
      })}
      onDragOver={(event) => {
        event.preventDefault();
      }}
      onDrop={onShipmentDrop}
      style={{
        height: "100%",
      }}
    >
      <ScheduleComponent
        ref={scheduleObjRef}
        cssClass="schedule-drag-drop"
        selectedDate={props.selectedDate}
        width="100%"
        height="calc(100% - 75px)"
        locale={i18n.language}
        allowInline={false}
        currentView={currentView}
        resourceHeaderTemplate={resourceHeaderTemplate}
        quickInfoTemplates={quickInfoTemplates}
        editorTemplate={editorTemplate}
        headerIndentTemplate={props.headerIndentTemplate}
        eventSettings={eventSettings}
        group={group}
        //   actionBegin={onActionBegin.bind(this)}
        timeScale={timeScale}
        rowAutoHeight
        allowDragAndDrop={true}
        allowResizing={false}
        cellDoubleClick={cellDoubleClick}
        cellClick={cellClick}
        navigating={navigating}
        drag={drag}
        dragStart={dragStart}
        dragStop={dragStop}
        eventRendered={onEventRendered}
        dataBound={() => sync()}
        workHours={workHours}
        workDays={workDays}
        showQuickInfo={false}
        eventClick={(args: EventClickArgs) => {
          const event = args.event as CalendarEvent;
          setSelectedEvent(event);
          setActiveShipmentFromEvent(
            shipmentsRef.current.find(
              (shipment) => shipment._id === event.shipmentId,
            ) ?? null,
          );
          const element = isArray(args.element)
            ? args.element[0]
            : args.element;
          const rect = element.getBoundingClientRect();
          setSelectedEventElement({
            getBoundingClientRect: () => rect,
          });
        }}
      >
        <ResourcesDirective>
          <ResourceDirective
            field="driverId"
            title="Consultant"
            name="Consultants"
            allowMultiple={false}
            dataSource={driverResourceData}
            textField="label"
            idField="Id"
            colorField="color"
          ></ResourceDirective>
        </ResourcesDirective>
        <ViewsDirective>
          <ViewDirective option="TimelineDay" displayName={t("timelineDay")} />
          <ViewDirective
            option="TimelineWeek"
            displayName={t("timelineWeek")}
          />
          <ViewDirective
            option="TimelineMonth"
            displayName={t("timelineMonth")}
          />
        </ViewsDirective>
        <Inject
          services={[TimelineViews, TimelineMonth, Resize, DragAndDrop]}
        />
      </ScheduleComponent>
      {selectedEvent ? (
        <ClickAwayListener
          onClickAway={(_event) => {
            setSelectedEvent(null);
            setSelectedEventElement(null);
          }}
          mouseEvent="onClick"
        >
          <Popper
            data-testid="planning-calendar-click-popover"
            open={!!selectedEvent && !!selectedEventElement}
            anchorEl={selectedEventElement}
            transition
            placement="auto"
            modifiers={[
              {
                name: "preventOverflow",
                enabled: true,
                options: {
                  altAxis: true,
                  padding: 8,
                },
              },
              {
                name: "flip",
                enabled: true,
                options: {
                  fallbackPlacements: ["top", "bottom", "left", "right"],
                },
              },
            ]}
            sx={{
              zIndex: 999,
            }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps}>
                <Card
                  data-testid={testIds.planningCalendarClickPopoverContent}
                  sx={{
                    maxHeight: "80vh",
                    overflow: "auto",
                  }}
                >
                  {quickInfoContentTemplate(selectedEvent) || <div></div>}
                </Card>
              </Fade>
            )}
          </Popper>
        </ClickAwayListener>
      ) : null}

      {hoveredShipment && !selectedEvent ? (
        <Popper
          data-testid="planning-calendar-hover-popover"
          open={!!hoveredShipment && !selectedEvent}
          anchorEl={hoverAnchorEl}
          placement="right-start"
          transition
          modifiers={[
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                altAxis: true,
                padding: 8,
              },
            },
            {
              name: "flip",
              enabled: true,
              options: {
                fallbackPlacements: ["top", "bottom", "left", "right"],
              },
            },
          ]}
          sx={{ zIndex: 998, maxHeight: "calc(100vh - 32px)", overflow: "auto" }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={150}>
              <Card data-testid={testIds.planningCalendarHoverPopoverContent}>
                {hoverQuickInfoContent(
                  shipmentEvents.find((e) => e.Id === hoveredShipment._id) ?? { shipmentId: hoveredShipment._id } as unknown as CalendarEvent
                )}
              </Card>
            </Fade>
          )}
        </Popper>
      ) : null}

      {activeShipmentFromEvent ? (
        <TripSeparationModal
          open={tripSeparationModalOpen}
          onClose={() => {
            setTripSeparationModalOpen(false);
            setActiveShipmentFromEvent(null);
          }}
          shipments={[
            activeShipmentFromEvent,
            ...activeShipmentFromEvent.otherShipmentsInTrip,
          ]}
          onRefresh={props.onRefresh}
          onShipmentsUncombine={onShipmentsUncombine}
        />
      ) : null}
    </div>
  );
};

export default PlanningCalendar;

const getTimeScaleForView = (view: View): TimeScaleModel => {
  return view === "TimelineWeek"
    ? {
        enable: true,
        interval: 24 * 60,
        slotCount: 8,
      }
    : view === "TimelineDay" || view === "Day"
    ? {
        enable: true,
        interval: 4 * 60,
        slotCount: 8,
      }
    : {
        enable: true,
        interval: 24 * 60,
        slotCount: 1,
      };
};

export const getDriveTimeDurations = (
  shipment: AugmentedShipment,
  showDriveTimes: boolean,
) => {
  const canHaveDriveTimes =
    showDriveTimes &&
    shipment.trip &&
    shipment.trip.status !== Status.Deleted &&
    shipment.trip.driver &&
    shipment.trip.firstPickupTime &&
    shipment.trip.lastDropoffTime;

  return {
    driveToPickupDuration:
      canHaveDriveTimes && shipment.trip?.etaFromPreviousTrip
        ? shipment.trip.etaFromPreviousTrip
        : 0,
    driveToNextTripDuration:
      canHaveDriveTimes && shipment.trip?.etaToNextTrip
        ? shipment.trip.etaToNextTrip
        : 0,
  };
};

const shipmentToEvents = (
  shipment: AugmentedShipment,
  isCommodityDriven: boolean,
  allShipments: AugmentedShipment[],
  showDriveTimes: boolean,
): CalendarEvent[] => {
  const receiverLocation = shipment.shipmentLocations.find(
    (location) =>
      location.locationType === ShipmentLocationType.DropOff ||
      location.locationType === ShipmentLocationType.DropForHook,
  );
  const shippedGoods = shipment.shipmentLocations.flatMap(
    (location) => location.shippedGoods,
  );
  const firstShippedGood = shippedGoods[0];

  const pickupTime =
    shipment.trip?.firstPickupTime || shipment.route?.firstPickupTime;
  const dropoffTime =
    shipment.trip?.lastDropoffTime || shipment.route?.lastDropoffTime;

  const formattedPickupTime = pickupTime
    ? pickupTime instanceof Date
      ? pickupTime.toISOString()
      : typeof pickupTime === "string"
      ? pickupTime
      : undefined
    : undefined;
  const formattedDropoffTime = dropoffTime
    ? dropoffTime instanceof Date
      ? dropoffTime.toISOString()
      : typeof dropoffTime === "string"
      ? dropoffTime
      : undefined
    : undefined;

  if (!formattedPickupTime || !formattedDropoffTime) {
    return [];
  }

  const { driveToPickupDuration, driveToNextTripDuration } =
    getDriveTimeDurations(shipment, showDriveTimes);

  // Suppress a drive segment if the adjacent shipment (same driver) already
  // renders the same drive time — prevents the segment from appearing twice
  // when two consecutive trips are both visible on the calendar.
  const sameDriverShipments = allShipments.filter(
    (s) =>
      s._id !== shipment._id &&
      s.trip?.driver === shipment.trip?.driver &&
      s.trip?.status !== Status.Deleted,
  );

  // Suppress driveToNext if the immediately next trip (same driver, earliest
  // firstPickupTime after this trip's lastDropoffTime) shares the exact same
  // drive duration (etaToNextTrip === etaFromPreviousTrip), meaning it's the
  // same drive segment. We prioritize showing driveToPickup on the next order.
  // We intentionally do NOT suppress driveToPickup, as that would create an
  // empty gap between the two orders on the calendar.
  const currentLastDropoff = new Date(shipment.trip?.lastDropoffTime).getTime();
  const nextTrip = sameDriverShipments
    .filter((s) => s.trip?.firstPickupTime && new Date(s.trip.firstPickupTime).getTime() > currentLastDropoff)
    .sort((a, b) => new Date(a.trip!.firstPickupTime).getTime() - new Date(b.trip!.firstPickupTime).getTime())[0];

  const driveToNextAlreadyCovered =
    driveToNextTripDuration > 0 &&
    !!nextTrip?.trip?.etaFromPreviousTrip &&
    nextTrip.trip.etaFromPreviousTrip === shipment.trip!.etaToNextTrip;

  // Expand the event's time span to include drive times so the whole block
  // occupies one calendar slot and stacks correctly with other shipments.
  // Always use the raw durations here — clamping to the view range is done
  // at display time in the event template so the stored durations stay accurate.
  const expandedPickupTime = driveToPickupDuration
    ? addSeconds(new Date(formattedPickupTime), -driveToPickupDuration).toISOString()
    : formattedPickupTime;

  const expandedDropoffTime = driveToNextTripDuration && !driveToNextAlreadyCovered
    ? addSeconds(new Date(formattedDropoffTime), driveToNextTripDuration).toISOString()
    : formattedDropoffTime;

  const tripEvent: CalendarEvent = {
    Id: shipment._id,
    type: CalendarEventType.TRIP,
    label: isCommodityDriven
      ? `#${shipment.shipmentNumber} ${shipment.otherShipmentsInTrip
          .map((s) => `#${s.shipmentNumber}`)
          .join(", ")} ${receiverLocation?.receiver?.code} / ${
          firstShippedGood?.label
        }`
      : `#${shipment.shipmentNumber} - ${shipment.originState} / ${shipment.destinationState} - ${shipment.customer.name}`,
    status: shipment.trip?.status || shipment.status,
    pickupTime: expandedPickupTime,
    dropoffTime: expandedDropoffTime,
    earliestPickup: shipment.earliestPickup,
    latestPickup: shipment.latestPickup,
    description: `${shipmentLocationAddressLabel(
      shipment.route?.locations?.[0],
    )} to ${shipmentLocationAddressLabel(last(shipment.route?.locations))}`,
    driverId: shipment.trip?.driver as string,
    shipmentId: shipment._id,
    shipmentNumber: shipment.shipmentNumber,
    rawPickupTime: formattedPickupTime,
    rawDropoffTime: formattedDropoffTime,
    plannedDwellSeconds: getPlannedDwellSeconds(shipment),
    driveToPickupDuration: driveToPickupDuration,
    driveToNextTripDuration: driveToNextAlreadyCovered ? 0 : driveToNextTripDuration,
  };

  return [tripEvent];
};

type AugmentedShipmentWithTrip = Omit<AugmentedShipment, "trip"> & {
  trip: NonNullable<AugmentedShipment["trip"]>;
};

export const AugmentedShipmentDetails = ({
  shipment,
  onRefresh,
}: {
  shipment: AugmentedShipment;
  onRefresh: PlanningToolProps["onRefresh"];
}) => {
  const [selectedShipment, setSelectedShipment] =
    useState<AugmentedShipment>(shipment);

  return (
    <Stack direction="row">
      {shipment.otherShipmentsInTrip.length ? (
        <Tabs data-testid={testIds.planningCalendarShipmentNavigation}
          orientation="vertical"
          value={selectedShipment}
          onChange={(_, value) => {
            setSelectedShipment(value);
          }}
          sx={{ borderRight: 1, borderColor: "divider" }}
        >
          {[shipment, ...shipment.otherShipmentsInTrip].map((shipment) => (
            <Tab data-testid={`${testIds.planningCalendarShipmentNumber}-${shipment.shipmentNumber}`} label={`#${shipment.shipmentNumber}`} value={shipment} />
          ))}
        </Tabs>
      ) : null}
      <Stack sx={{ display: "flex", flex: 1, flexDirection: "column" }}>
        <ShipmentInfosContainer
          shipmentId={selectedShipment._id}
          onLoad={() => {}}
          actionsInNewTab={true}
          onDeleted={(shipmentId) => {
            onRefresh([shipmentId]);
          }}
          onRefresh={async () => {
            // Refresh both the shipment and the planning tool's shipment list
            // This ensures that when a trip is deleted, the planning tool updates immediately
            await onRefresh([selectedShipment._id]);
          }}
          canAssign={false}
        />
      </Stack>
    </Stack>
  );
};

const CalendarTripDetails = ({ shipment }: { shipment: AugmentedShipment }) => {
  if (!shipment.trip) {
    return null;
  }
  const tripSummaryData = tripSummaryDataForAugmentedShipment(
    shipment as AugmentedShipmentWithTrip,
  );

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      flex={1}
      spacing={3}
    >
      <Box
        sx={{
          flex: 1,
        }}
      >
        <CalendarTripSummary shipment={shipment} />
      </Box>
      <Box
        sx={{
          pb: 2,
          height: 300,
          flex: 2,
        }}
      >
        <TripRoute trip={tripSummaryData} />
      </Box>
    </Stack>
  );
};

const CalendarTripSummary = ({ shipment }: { shipment: AugmentedShipment }) => {
  if (!shipment.trip) {
    return null;
  }
  const tripSummaryData = tripSummaryDataForAugmentedShipment(
    shipment as AugmentedShipmentWithTrip,
  );

  return <TripSummary trip={tripSummaryData} readonly />;
};

const tripSummaryDataForAugmentedShipment = (
  shipment: AugmentedShipmentWithTrip,
) => {
  return {
    ...shipment.trip,
    shipments: [shipment, ...(shipment.otherShipmentsInTrip || [])],
    driver: shipment.trip?.driver
      ? {
          _id: shipment.trip.driver,
          firstname: "",
          lastname: "",
        }
      : null,
    tractor: shipment.trip?.tractor
      ? {
          _id: shipment.trip.tractor,
          serialNumber: "",
        }
      : null,
    trailer: shipment.trip?.trailer
      ? {
          _id: shipment.trip.trailer,
          serialNumber: "",
          type: TrailerType.Tanker,
        }
      : null,
    carrier: shipment.trip?.carrier
      ? {
          _id: shipment.trip.carrier,
          name: "",
        }
      : null,
  };
};

const workHours = {
  start: "00:00",
  end: "23:59",
  highlight: true,
};

const workDays = [0, 1, 2, 3, 4, 5, 6];

const getCellGroupIndex = (element: Element) =>
  parseInt(element.getAttribute("data-group-index") || "-1", 10);

const getCellStartTime = (element: Element) =>
  new Date(parseFloat(element.getAttribute("data-date") || "0"));

const getCellEndTime = (element: Element) => {
  if (element.nextSibling) {
    return getCellStartTime(element.nextSibling as Element);
  }

  if (element.previousSibling) {
    const previousSiblingStartTime = getCellStartTime(
      element.previousSibling as Element,
    );
    const elementStartTime = getCellStartTime(element);
    const duration =
      elementStartTime.getTime() - previousSiblingStartTime.getTime();

    return addMilliseconds(elementStartTime, duration);
  }

  return addMinutes(getCellStartTime(element), 30);
};

export const shiftAppliesToDriver = (
  shift: PlanningToolProps["driverShifts"][0],
  driverId: string,
  domicileId: string,
) => {
  if (shift.isAdhoc) {
    return false;
  }
  return (
    (!shift.driverIds?.length &&
      (!shift.domicileId || shift.domicileId === domicileId)) ||
    shift.driverIds?.includes(driverId)
  );
};
